import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { getPODetails } from "../../Services/PoException";
import { Link } from "react-router-dom";
import { NotificationManager, NotificationContainer } from "react-notifications";
import "react-notifications/lib/notifications.css";

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#3f506b",
  "& th": {
    color: "#fff",
    fontWeight: "bold",
  },
});

const StyledButton = styled(Button)({
  marginTop: "16px",
  padding: "14px 16px",
  backgroundColor: "#3f506b",
  "&:hover": {
    backgroundColor: "#115293",
  },
});

const StyledTextField = styled(TextField)({
  marginRight: "16px",
  marginTop: "16px",
});

const StyledContainer = styled(Container)({
  maxWidth: "2000px",
});

const POPage = () => {
  const [poNumber, setPoNumber] = useState("");
  const [poData, setPoData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setPoData(null);
    try {
      const fetchedData = await getPODetails(poNumber);
      if (
        !fetchedData ||
        Object.keys(fetchedData).length === 0 ||
        !fetchedData.exceptions
      ) {
        NotificationManager.warning("No records found.", "Warning", 3000);
        setPoData(null);
      } else {
        setPoData(fetchedData);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        NotificationManager.warning("No records found.", "Warning", 3000);
      } else {
        NotificationManager.error(
          "Error fetching PO details. Please try again.",
          "Error",
          3000
        );
      }
      console.error("Error checking PO number:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      {loading && <LinearProgress sx={{ marginBottom: 2 }} />}
      <Divider sx={{ marginBottom: 2, borderBottomWidth: 3 }} />
      <StyledTextField
        label="PO Search"
        variant="outlined"
        value={poNumber}
        onChange={(e) => setPoNumber(e.target.value)}
        required
      />
      <StyledButton
        variant="contained"
        color="primary"
        onClick={handleSearch}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={24} sx={{ color: "white" }} />
        ) : (
          "Submit"
        )}
      </StyledButton>

      {poData && (
        <>
          <Typography variant="h6" sx={{ marginTop: 4 }}>
            PO Details
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Dept</TableCell>
                  <TableCell>Vendor Name</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Order Type</TableCell>
                  <TableCell>PO Status</TableCell>
                  <TableCell>PO Date</TableCell>
                  <TableCell>Approved Date</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{poData.po_number || ""}</TableCell>
                  <TableCell>{poData.department_id || ""}</TableCell>
                  <TableCell>{poData.vendor_name || ""}</TableCell>
                  <TableCell>{poData.chain || ""}</TableCell>
                  <TableCell>{poData.po_type || ""}</TableCell>
                  <TableCell>{poData.po_status || ""}</TableCell>
                  <TableCell>
                    {new Date(poData.start_ship_date)
                      .toISOString()
                      .split("T")[0] || ""}
                  </TableCell>
                  <TableCell>{poData.requestDate || ""}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {poData.exceptions && poData.exceptions.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ marginTop: 4 }}>
                PO Exceptions
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Stores</TableCell>
                      <TableCell>Request Date</TableCell>
                      <TableCell>Approve Date/Denied Date</TableCell>
                      <TableCell>Routing Status</TableCell>
                      <TableCell>PO Type</TableCell>
                      <TableCell>Supplier Site Id</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {poData.exceptions.map((exception, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Link to={`/exceptions/${exception.po_exception_id}`}>
                            {exception.po_number || ""}
                          </Link>
                        </TableCell>
                        <TableCell>{`${exception.store_list || ""} - ${
                          exception.name || ""
                        }`}</TableCell>
                        <TableCell>
                          {new Date(exception.created_at)
                            .toISOString()
                            .split("T")[0] || ""}
                        </TableCell>
                        <TableCell>{exception.approve_date || ""}</TableCell>
                        <TableCell>{exception.status || ""}</TableCell>
                        <TableCell>{poData.po_type || ""}</TableCell>
                        <TableCell>
                          {exception.supplier_site_id || ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            NotificationManager.warning("No exceptions found.", "Warning", 30000)
          )}
        </>
      )}
       <NotificationContainer />
    </Container>
  );
};

export default POPage;
